import { useState } from 'react';
import { certLookup } from '../../../utils/api';
import { fetchCertImages } from '../../../utils/certImages';

export const useCertLookup = ({ rows, setRows, addLog }) => {
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });
  // Maximum number of concurrent requests
  const MAX_CONCURRENT = 20;

  const fetchCertDetails = async (row, index, service, isPCGSNumberLookup = false) => {
    try {
      // Use PCGS number if this is a PCGS # lookup, otherwise use certification number
      const certNumber = isPCGSNumberLookup ? row.pcgs_number : row.certification_number;

      addLog(`Looking up ${service.name} ${certNumber}${isPCGSNumberLookup ? ' (PCGS #)' : ''}...`);
      
      let grade = row.grade;
      if (service.name.toLowerCase() === 'ngc' && !grade) {
        // Prompt for grade if it's NGC and grade is missing
        const promptedGrade = prompt(`Enter grade for NGC cert ${certNumber}:`);
        if (!promptedGrade) {
          addLog(`Skipping ${certNumber} - grade required for NGC lookup`, 'warning');
          return { index, success: false };
        }
        grade = promptedGrade;
      }

      const result = await certLookup.lookup(
        service.name.toLowerCase(),
        certNumber,
        grade,
        isPCGSNumberLookup
      );

      // Format images to match expected structure
      const formattedImages = result.images?.map((img, index) => {
        const imageUrl = img.url || img.image_url;
        let imageType = img.type;

        // If no type provided, determine based on service
        if (!imageType) {
          if (service.name.toLowerCase() === 'ngc') {
            // NGC alternates between obverse and reverse
            imageType = index % 2 === 0 ? 'coin_obverse' : 'coin_reverse';
          } else {
            // PCGS uses Image-1, Image-2 format
            imageType = `Image-${index + 1}`;
          }
        }

        return {
          image_url: imageUrl,
          image_type: imageType,
          image_number: index + 1
        };
      }).filter(img => img.image_url) || [];

      // Format CAC sticker data
      const formattedCacSticker = typeof result.cac_sticker === 'object' 
        ? result.cac_sticker?.type?.toLowerCase() 
        : (result.cac_sticker?.toLowerCase() || 'none');

      // Prepare updated data
      let updatedData;
      if (isPCGSNumberLookup) {
        // For PCGS # lookup, don't set certification_service_id or certification_number
        const { certification_service_id, certification_number, cac_sticker, ...otherResults } = result;
        updatedData = {
          ...row,
          ...otherResults,
          images: formattedImages,
          cac_sticker: formattedCacSticker
        };
      } else {
        // For regular lookup, include all fields
        const { cac_sticker, ...otherResults } = result;
        updatedData = {
          ...row,
          ...otherResults,
          certification_service_id: service.id,
          images: formattedImages,
          cac_sticker: formattedCacSticker
        };
      }

      return {
        index,
        success: true,
        data: updatedData
      };
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      addLog(`Failed to fetch details for ${certNumber}: ${errorMessage}`, 'error');
      return { index, success: false };
    }
  };

  const handleFetchCertDetails = async (services) => {
    // Prepare fetch tasks
    const fetchTasks = rows.map((row, index) => {
      // Handle PCGS number lookup if cert # is empty but PCGS # exists
      if (!row.certification_number && row.pcgs_number) {
        const pcgsService = services.find(s => s.name.toLowerCase() === 'pcgs');
        if (pcgsService) {
          return {
            row: { ...row },
            index,
            service: pcgsService,
            isPCGSNumberLookup: true
          };
        }
      }
      
      // Regular certification lookup
      if (!row.certification_service_id || !row.certification_number) return null;
      const service = services.find(s => s.id === parseInt(row.certification_service_id));
      if (!service) return null;
      return {
        row: { ...row },
        index,
        service,
        isPCGSNumberLookup: false
      };
    }).filter(task => task !== null);

    if (fetchTasks.length === 0) {
      addLog('No certification numbers to fetch.');
      return;
    }

    setFetchProgress({ current: 0, total: fetchTasks.length });
    addLog('Fetching certification details...');
    const newRows = [...rows];
    let fetchCount = 0;
    let errorCount = 0;

    // Process tasks in batches
    for (let i = 0; i < fetchTasks.length; i += MAX_CONCURRENT) {
      const batch = fetchTasks.slice(i, i + MAX_CONCURRENT);
      const results = await Promise.all(
        batch.map(task => fetchCertDetails(task.row, task.index, task.service, task.isPCGSNumberLookup))
      );

      // Process batch results
      results.forEach(result => {
        if (result.success) {
          newRows[result.index] = result.data;
          fetchCount++;
        } else {
          errorCount++;
        }
        setFetchProgress(prev => ({ ...prev, current: fetchCount + errorCount }));
      });

      // Update rows after each batch
      setRows([...newRows]);
    }

    addLog(`Completed fetching details. ${fetchCount} successful, ${errorCount} failed.`);
    setFetchProgress({ current: 0, total: 0 }); // Reset progress when complete
  };

  return {
    handleFetchCertDetails,
    fetchProgress
  };
};
